import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { agentApi } from '@/api/agent.api';
import { applicationApi } from '@/api/application.api';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';

const NAME_MAP_PAGE_SIZE = 500;
const STALE_TIME = 5 * 60 * 1000;

/**
 * 全站共享的 agentId → name 映射：合并 Application（INSTANCE 绑定）与 Agent 模版（TEMPLATE 绑定）两侧的列表。
 * 使用稳定的 queryKey 让多处调用共享同一份缓存，避免重复请求。
 */
export function useAgentNameMap(): Record<string, string> {
  const { data: appsRes } = useQuery({
    queryKey: ['agent-name-map', 'applications'] as const,
    queryFn: () =>
      applicationApi.list(create(ListRequestSchema, { page: 1, pageSize: NAME_MAP_PAGE_SIZE })),
    staleTime: STALE_TIME,
  });
  const { data: agentsRes } = useQuery({
    queryKey: ['agent-name-map', 'agents'] as const,
    queryFn: () =>
      agentApi.listAgents(create(ListRequestSchema, { page: 1, pageSize: NAME_MAP_PAGE_SIZE })),
    staleTime: STALE_TIME,
  });
  return useMemo(() => {
    const map: Record<string, string> = {};
    for (const a of appsRes?.data ?? []) {
      if (a?.id && a.name) map[a.id] = a.name;
    }
    for (const a of agentsRes?.data ?? []) {
      if (a?.id && a.name) map[a.id] = a.name;
    }
    return map;
  }, [appsRes, agentsRes]);
}
